import { useSearchParams } from 'react-router';
import { useSelectedEnvironmentKey } from '@gonfalon/app-context/useSelectedEnvironmentKey';
import { hasLegacyPlan as hasLegacyPlanFlag } from '@gonfalon/constants/constants';
import {
  experimentPaginationLimit,
  isABTestingEnabled,
  isExperimentationApiEnabled,
} from '@gonfalon/dogfood-flags/dogfood-flags';
import { useExperiments } from '@gonfalon/experiments/useExperiments';
import { useProjectKey } from '@gonfalon/router/useProjectKey';

import { useSubscription } from '#hooks/useSubscription';

/**
 * Hook determines whether to enable the post-experimentation experience
 * Post-experimentation = users who had experimentation access and no longer do
 *
 * Post-experimentation experience: banner & visible experiments/metrics tables on
 * "Overview" & "Metrics" and Flag->Experiments view.
 * User can't modify experiments but can stop existing running experiments.
 *
 * Fetches experiments.
 * Evaluates the enable-new-post-experimentation-experience flag.
 */
export const useEnablePostExperimentationExperience = () => {
  const { isReady: isSubscriptionReady, entity: subscription } = useSubscription();

  const projKey = useProjectKey();
  const envKey = useSelectedEnvironmentKey();
  const [searchParams] = useSearchParams();

  const hasLegacyPlan = hasLegacyPlanFlag();
  const enableExperimentation = isABTestingEnabled();

  const isEnterpriseCampaignExpired = !!subscription.getEnterpriseCampaign()?.isExpired();

  const { data: modernExperiments, status: experimentsRequestStatus } = useExperiments(
    {
      projectKey: projKey,
      environmentKey: envKey,
      params: {
        limit: Number(searchParams.get('limit') ?? experimentPaginationLimit()),
        offset: Number(searchParams.get('offset') ?? 0),
        lifecycleState: ['active' as const],
      },
    },
    { enabled: isExperimentationApiEnabled() },
  );
  const modernExperimentsCount = modernExperiments?.total_count ?? 0;

  const isRequestsReady = isSubscriptionReady && !['idle', 'loading'].includes(experimentsRequestStatus);

  const disableCases = [hasLegacyPlan, enableExperimentation, modernExperimentsCount === 0];

  const enableCases = [isEnterpriseCampaignExpired, modernExperimentsCount > 0];

  const enablePostExperimentationExperience = () => {
    if (disableCases.some((v) => !!v)) {
      return false;
    }

    // disableCases must be false:
    // - user can't be on a legacy plan
    // - experimentation can't be enabled
    // - the flag must be enabled
    // - user must have modern experiments
    if (enableCases.some((v) => !!v)) {
      return true;
    }

    return false;
  };

  return {
    enablePostExperimentationExperience: enablePostExperimentationExperience(),
    isPostExperimentationExperienceReady: isRequestsReady,
  };
};
